import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { ZoomIn, ZoomOut, Check, X, Settings } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface DXFPreviewProps {
  data: {
    // XML format (new)
    measurements?: {
      area_sqft: number;
      pitch: string;
      pitches?: string[];
      ridge_lf: number;
      valley_lf: number;
      hip_lf: number;
      eave_lf: number;
      rake_lf: number;
      stepflashing_lf: number;
      flashing_lf: number;
    };
    facets?: Array<{
      id: string;
      area: number;
      pitch: string;
    }>;
    // DXF format (old)
    previewSvg?: string;
    summary?: {
      totalPlanAreaSqFt: number;
      totalSurfaceAreaSqFt: number;
      squares: number;
      wasteFactor: number;
      pitchStrategy: string;
    };
    lengthsLF?: {
      eave: number;
      rake: number;
      ridge: number;
      hip: number;
      valley: number;
      wallStepFlashing: number;
      dripEdge: number;
    };
    penetrations?: {
      pipes: Array<{ diamIn: number }>;
      skylights: Array<{ wIn: number; hIn: number }>;
      chimneys: Array<{ perimeterLF: number }>;
      fans: number;
    };
    diagnostics: {
      facetCount?: number;
      lineCount?: number;
      unmappedLayers?: string[];
      warnings: string[];
      errors: string[];
    };
  };
  onAccept: () => void;
  onReject: () => void;
  onAdjustMapping?: () => void;
  onScaleCalibration?: () => void;
}

export const DXFPreview = ({
  data,
  onAccept,
  onReject,
  onAdjustMapping,
  onScaleCalibration
}: DXFPreviewProps) => {
  const [zoom, setZoom] = useState(1);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.5));

  // Check if this is XML data (new format) or DXF data (old format)
  const isXmlData = data.measurements && !data.summary;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Preview */}
      <Card className="lg:col-span-2 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {isXmlData ? 'GAF QuickMeasure Summary' : 'Roof Plan Preview'}
          </h3>
          {!isXmlData && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleZoomOut}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={handleZoomIn}>
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {isXmlData ? (
          // XML summary display
          <div className="border rounded-lg p-6 bg-muted/20 space-y-4">
            <div className="text-center">
              <Check className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h4 className="text-2xl font-bold mb-2">
                {data.measurements!.area_sqft} sq ft
              </h4>
              <p className="text-muted-foreground">Total Roof Area</p>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Dominant Pitch:</p>
                <p className="font-semibold text-lg">{data.measurements!.pitch}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Facets:</p>
                <p className="font-semibold text-lg">{data.diagnostics.facetCount || 0}</p>
              </div>
            </div>

            {data.measurements!.pitches && data.measurements!.pitches.length > 1 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">All Pitches:</p>
                <div className="flex flex-wrap gap-2">
                  {data.measurements!.pitches.map((pitch) => (
                    <Badge key={pitch} variant="secondary">{pitch}</Badge>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            <div className="space-y-2">
              <h5 className="font-semibold">Linear Measurements</h5>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {data.measurements!.ridge_lf > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ridge:</span>
                    <span className="font-medium">{data.measurements!.ridge_lf} LF</span>
                  </div>
                )}
                {data.measurements!.valley_lf > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Valley:</span>
                    <span className="font-medium">{data.measurements!.valley_lf} LF</span>
                  </div>
                )}
                {data.measurements!.hip_lf > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Hip:</span>
                    <span className="font-medium">{data.measurements!.hip_lf} LF</span>
                  </div>
                )}
                {data.measurements!.eave_lf > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Eave:</span>
                    <span className="font-medium">{data.measurements!.eave_lf} LF</span>
                  </div>
                )}
                {data.measurements!.rake_lf > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rake:</span>
                    <span className="font-medium">{data.measurements!.rake_lf} LF</span>
                  </div>
                )}
                {data.measurements!.stepflashing_lf > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Step Flashing:</span>
                    <span className="font-medium">{data.measurements!.stepflashing_lf} LF</span>
                  </div>
                )}
                {data.measurements!.flashing_lf > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Flashing:</span>
                    <span className="font-medium">{data.measurements!.flashing_lf} LF</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // DXF SVG preview
          <div className="border rounded-lg overflow-auto bg-muted/20" style={{ maxHeight: '500px' }}>
            <div
              className="flex items-center justify-center p-4"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
              dangerouslySetInnerHTML={{ __html: data.previewSvg || '' }}
            />
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <Button onClick={onAccept} className="flex-1">
            <Check className="h-4 w-4 mr-2" />
            Accept & Import
          </Button>
          <Button onClick={onReject} variant="outline" className="flex-1">
            <X className="h-4 w-4 mr-2" />
            Reject
          </Button>
        </div>

        {!isXmlData && (
          <div className="flex gap-2 mt-2">
            {onAdjustMapping && (
              <Button onClick={onAdjustMapping} variant="secondary" className="flex-1">
                <Settings className="h-4 w-4 mr-2" />
                Adjust Layer Mapping
              </Button>
            )}
            {onScaleCalibration && (
              <Button onClick={onScaleCalibration} variant="secondary" className="flex-1">
                <Settings className="h-4 w-4 mr-2" />
                Calibrate Scale
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Summary & Diagnostics */}
      <Card className="p-6">
        <ScrollArea className="h-[580px]">
          <div className="space-y-4">
            {isXmlData ? (
              // XML facet breakdown
              <>
                {data.facets && data.facets.length > 0 && (
                  <>
                    <div>
                      <h3 className="font-semibold mb-3">Facet Breakdown</h3>
                      <div className="space-y-2 text-sm">
                        {data.facets.map((facet) => (
                          <div key={facet.id} className="flex justify-between items-center">
                            <span className="text-muted-foreground">{facet.id}:</span>
                            <div className="text-right">
                              <span className="font-medium">{facet.area} sq ft</span>
                              <Badge variant="outline" className="ml-2">{facet.pitch}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Separator />
                  </>
                )}
              </>
            ) : (
              // DXF summary
              <>
                <div>
                  <h3 className="font-semibold mb-3">Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Plan Area:</span>
                      <span className="font-medium">{data.summary!.totalPlanAreaSqFt.toFixed(1)} sq ft</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Surface Area:</span>
                      <span className="font-medium">{data.summary!.totalSurfaceAreaSqFt.toFixed(1)} sq ft</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Squares:</span>
                      <span className="font-medium text-lg">{data.summary!.squares.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Waste Factor:</span>
                      <span className="font-medium">{data.summary!.wasteFactor}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Pitch:</span>
                      <span className="font-medium">{data.summary!.pitchStrategy.replace('global:', '')}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="font-semibold mb-3">Linear Footage</h3>
                  <div className="space-y-2 text-sm">
                    {Object.entries(data.lengthsLF!).map(([key, value]) => (
                      value > 0 && (
                        <div key={key} className="flex justify-between">
                          <span className="text-muted-foreground capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </span>
                          <span className="font-medium">{value.toFixed(1)} LF</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="font-semibold mb-3">Penetrations</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Pipes:</span>
                      <span className="font-medium">{data.penetrations!.pipes.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Skylights:</span>
                      <span className="font-medium">{data.penetrations!.skylights.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Chimneys:</span>
                      <span className="font-medium">{data.penetrations!.chimneys.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Fans/Vents:</span>
                      <span className="font-medium">{data.penetrations!.fans}</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {(data.diagnostics.warnings.length > 0 || 
              data.diagnostics.errors.length > 0 || 
              (data.diagnostics.unmappedLayers && data.diagnostics.unmappedLayers.length > 0)) && (
              <>
                <Separator />
                <div>
                  <h3 className="font-semibold mb-3">Diagnostics</h3>
                  
                  {data.diagnostics.errors.length > 0 && (
                    <Alert variant="destructive" className="mb-3">
                      <AlertDescription>
                        <ul className="list-disc list-inside text-sm">
                          {data.diagnostics.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {data.diagnostics.warnings.length > 0 && (
                    <Alert className="mb-3">
                      <AlertDescription>
                        <ul className="list-disc list-inside text-sm">
                          {data.diagnostics.warnings.map((warn, i) => (
                            <li key={i}>{warn}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {data.diagnostics.unmappedLayers && data.diagnostics.unmappedLayers.length > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Unmapped Layers:</p>
                      <div className="flex flex-wrap gap-2">
                        {data.diagnostics.unmappedLayers.map((layer, i) => (
                          <Badge key={i} variant="secondary">{layer}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {!isXmlData && (
              <>
                <Separator />

                <div>
                  <h3 className="font-semibold mb-2">Layer Legend</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#e0e0e0' }} />
                      <span>Facet</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#2196F3' }} />
                      <span>Eave</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#FF9800' }} />
                      <span>Rake</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#F44336' }} />
                      <span>Ridge</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#9C27B0' }} />
                      <span>Hip</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#4CAF50' }} />
                      <span>Valley</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </Card>
    </div>
  );
};
